import GridTable from '../../table/GridTable';
import {
  ColDef,
  ICellRendererParams,
  RowDoubleClickedEvent,
} from 'ag-grid-community';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  formatDate,
  formatNumberWithComma,
  updateUrlWithParams,
} from '../../../util/util';
import { AgGridReact } from 'ag-grid-react';
import queryString from 'query-string';
import { IFranchise, useFranchiseStore } from '../../../store/useFranchise';
import FranchiseFilter from './filter/FranchiseFilter';
import Button from '../../button/Button';
import ChargeModal from './ChargeModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import apiClient from '../../../service/apiClient';
import { toast } from 'react-toastify';
import {ResponseError} from "../../../type/type";
import {isAxiosError} from "axios";
import dayjs from 'dayjs';

/**
 * @author taulmax
 * @date 2024.12.12
 * @description 가맹점 페이지
 */
const Franchise = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [franchiseId, setFranchiseId] = useState<string>('');
  const [companyName, setCompanyName] = useState<string>('');
  const [selectedCount, setSelectedCount] = useState<number>(0);

  const { franchises, franchisesPage, getFranchiseLoading, getFranchise } =
    useFranchiseStore();

  const companyNameRef = useRef<HTMLInputElement>(null);
  const companyNumberRef = useRef<HTMLInputElement>(null);
  const ceoRef = useRef<HTMLInputElement>(null);
  const telRef = useRef<HTMLInputElement>(null);
  const idRef = useRef<HTMLInputElement>(null);
  const bizRef = useRef<HTMLInputElement>(null);

  const gridRef = useRef<AgGridReact<IFranchise>>(null);

  const colDefs: ColDef<IFranchise>[] = [
    {
      headerName: '선택',
      field: 'id',
      checkboxSelection: true,
      headerCheckboxSelection: true,
      width: 50,
      maxWidth: 50,
    },
    {
      headerName: '아이디',
      field: 'customerId',
      flex: 1.5
    },
    {
      headerName: '권한',
      field: 'role',
      flex: 1,
      cellRenderer: (props: ICellRendererParams) => {
        const roleMap: { [key: string]: { text: string; color: string } } = {
          ROLE_ADMIN: { text: '관리자', color: 'text-red-600' },
          ROLE_USER: { text: '일반', color: 'text-blue-600' },
          ROLE_CS: { text: 'CS', color: 'text-green-600' },
          ROLE_TRACKING: { text: '트래킹', color: 'text-purple-600' },
          ROLE_JOINED: { text: '대기', color: 'text-gray-500' },
          ROLE_MARKETING: { text: '내부 마케팅', color: 'text-amber-600' },
        };
        const role = roleMap[props.value] || { text: props.value, color: 'text-gray-600' };
        return <span className={`font-semibold ${role.color}`}>{role.text}</span>;
      },
    },
    {
      headerName: '회사명',
      field: 'companyName',
      flex: 1.5,
    },
    {
      headerName: '비즈프로필',
      field: 'bizProfileName',
      flex: 1,
    },
    {
      headerName: '카카오 채널 연동',
      field: 'bizmProfileKey',
      flex: 1.2,
      cellRenderer: (props: ICellRendererParams) => {
        const hasKey = !!props.data?.bizmProfileKey;
        if (hasKey) {
          return (
            <span className="inline-block px-2 py-0.5 text-xs font-semibold rounded-full border bg-green-100 text-green-700 border-green-200">
              연동완료
            </span>
          );
        }
        return (
          <span className="inline-block px-2 py-0.5 text-xs font-semibold rounded-full border bg-gray-100 text-gray-700 border-gray-200">
            연동안됨
          </span>
        );
      },
    },
    {
      headerName: '대표자명',
      field: 'ceo',
      flex: 1,
    },
    {
      headerName: '사업자번호',
      field: 'companyNumber',
      flex: 1,
    },
    {
      headerName: '대표전화',
      field: 'telNo',
      flex: 1,
    },
    // 중복 제거: 생성일 왼쪽의 비즈프로필 컬럼 삭제
    {
      headerName: '생성일',
      field: 'createdAt',
      valueFormatter: param => formatDate(param.value).timestamp,
      flex: 2,
    },
    {
      headerName: '수정일',
      field: 'updatedAt',
      valueFormatter: param => formatDate(param.value).timestamp,
      flex: 2,
    },
    {
      headerName: '보유 복비',
      field: 'chargeAmount',
      valueFormatter: param => formatNumberWithComma(param.value) + '원',
      flex: 1,
    },
    {
      headerName: '총 주문',
      field: 'contractCount',
      valueFormatter: param => param.value,
      flex: 1,
    },
    {
      headerName: '충전',
      field: 'id',
      cellRenderer: (props: ICellRendererParams) => {
        if (props.value) {
          return (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setIsOpen(true);
                setFranchiseId(props.value);
                setCompanyName(props.data.companyName);
              }}
              className="!px-3 !py-1.5 !bg-green-500 hover:!bg-green-600 !text-white !text-xs !font-medium !rounded !transition-colors"
            >
              복비 충전
            </button>
          );
        }
      },
      flex: 1,
    },
    {
      headerName: '수정',
      cellRenderer: (props: ICellRendererParams) => {
        if (props.data?.id) {
          return (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/franchise/update/${props.data.id}`);
              }}
              className="!px-3 !py-1.5 !bg-blue-500 hover:!bg-blue-600 !text-white !text-xs !font-medium !rounded !transition-colors"
            >
              수정
            </button>
          );
        }
      },
      flex: 0.7,
      minWidth: 80,
      maxWidth: 100,
    },
  ];

  const onRowDoubleClicked = useCallback(
    (params: RowDoubleClickedEvent<IFranchise>) => {
      const franchiseId = params.data?.id;
      if (franchiseId) {
        const endDt = dayjs().format('YYYY-MM-DDTHH:mm:ss');
        const startDt = dayjs().subtract(1, 'month').format('YYYY-MM-DDTHH:mm:ss');
        navigate(`/bokbi/bokbi-statistics?endDt=${endDt}&franchiseId=${franchiseId}&startDt=${startDt}`);
      }
    },
    [navigate]
  );

  useEffect(() => {
    const parsedQuery = queryString.parse(location.search);
    const companyName = (parsedQuery.companyName as string) || '';
    const ceo = (parsedQuery.ceo as string) || '';
    const telNo = (parsedQuery.telNo as string) || '';
    const companyNumber = (parsedQuery.companyNumber as string) || '';
    const id = (parsedQuery.id as string) || '';
    const page = parsedQuery.page ? Number(parsedQuery.page) : 0;
    const size = parsedQuery.size ? Number(parsedQuery.size) : 20;
    const bizProfileName = (parsedQuery.bizProfileName as string) || '';

    getFranchise({
      companyName,
      ceo,
      telNo,
      companyNumber,
      id,
      page,
      size,
      bizProfileName,
    });
  }, [getFranchise, location.search]);

  const onPageChange = useCallback(
    (page: number) => {
      const parsedQuery = queryString.parse(location.search);
      const updatedQuery = { ...parsedQuery, page: page.toString() };

      updateUrlWithParams(updatedQuery, navigate);
      getFranchise({
        companyName: (parsedQuery.companyName as string) || '',
        ceo: (parsedQuery.ceo as string) || '',
        telNo: (parsedQuery.telNo as string) || '',
        companyNumber: (parsedQuery.companyNumber as string) || '',
        id: (parsedQuery.id as string) || '',
        page,
        size: parseInt(parsedQuery.size as string) || 20,
      });
    },
    [location.search, navigate, getFranchise]
  );

  const rowSelection = 'multiple';

  const onSelectionChanged = useCallback(() => {
    const selectedRows = gridRef.current?.api.getSelectedRows();
    setSelectedCount(selectedRows?.length || 0);
  }, []);

  const handleRoleChange = async (role: string) => {
    const selectedNodes = gridRef.current?.api.getSelectedNodes();
    if (!selectedNodes || selectedNodes.length === 0) {
      toast.error('권한을 변경할 사용자를 선택해주세요.');
      return;
    }

    // 관리자 권한 체크
    const hasAdmin = selectedNodes.some(node => node.data?.role === 'ROLE_ADMIN');
    if (hasAdmin) {
      toast.error('관리자 권한은 변경할 수 없습니다.');
      return;
    }

    const customerIds = selectedNodes.map(node => node.data?.customerDbId).filter(Boolean);
    
    try {
      await apiClient.patch('/customer/role', {
        customerIds,
        role,
      });
      toast.success('권한이 변경되었습니다.');
      
      // 페이지 새로고침
      const parsedQuery = queryString.parse(location.search);
      getFranchise({
        companyName: (parsedQuery.companyName as string) || '',
        ceo: (parsedQuery.ceo as string) || '',
        telNo: (parsedQuery.telNo as string) || '',
        companyNumber: (parsedQuery.companyNumber as string) || '',
        page: parseInt(parsedQuery.page as string) || 0,
        size: parseInt(parsedQuery.size as string) || 20,
      });
    } catch (error) {
      if (isAxiosError<ResponseError>(error)) {
        toast.error(error.response?.data.errorMsg);
      } else {
        console.error('권한 변경에 실패했습니다:', error);
        toast.error('권한 변경에 실패했습니다.');
      }
    }
  };

  return (
    <div className="!w-full !h-full !flex !flex-col">
      <div className="!py-4">
        <FranchiseFilter
          companyNameRef={companyNameRef}
          companyNumberRef={companyNumberRef}
          ceoRef={ceoRef}
          telRef={telRef}
          idRef={idRef}
          bizRef={bizRef}
        />
        <div className="!flex !justify-end !gap-2 !mt-3">
          {selectedCount > 0 && (
            <span className="!text-sm !text-gray-600 !self-center !mr-2">
              {selectedCount}개 선택됨
            </span>
          )}
          <button
            type="button"
            onClick={() => handleRoleChange('ROLE_USER')}
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors whitespace-nowrap"
          >
            일반 등업
          </button>
          <button
            type="button"
            onClick={() => handleRoleChange('ROLE_MARKETING')}
            className="px-3 py-1.5 text-sm bg-amber-600 text-white rounded hover:bg-amber-700 transition-colors whitespace-nowrap"
          >
            마케팅 등업
          </button>
          <button
            type="button"
            onClick={() => handleRoleChange('ROLE_JOINED')}
            className="px-3 py-1.5 text-sm bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors whitespace-nowrap"
          >
            대기 전환
          </button>
        </div>
      </div>

      <div className="!flex-1 !min-h-0">
        <GridTable<IFranchise>
          gridRef={gridRef}
          height="100%"
          isLoading={getFranchiseLoading}
          rowData={franchises}
          columnDefs={colDefs}
          rowSelection={rowSelection}
          onRowDoubleClicked={onRowDoubleClicked}
          onSelectionChanged={onSelectionChanged}
        />
      </div>

      {/* 페이지네이션 */}
      {franchisesPage && franchisesPage.totalPages > 1 && (
        <div className="!w-full !mt-6">
          <div className="!flex !justify-center !items-center !space-x-1">
            {/* 맨 처음으로 */}
            <button
              onClick={() => onPageChange(0)}
              disabled={franchisesPage.pageable.pageNumber === 0}
              className="!px-2 !py-1.5 !text-sm !rounded !border !border-gray-300 hover:!bg-gray-50 disabled:!opacity-50 disabled:!cursor-not-allowed !transition-colors !duration-200"
            >
              <FontAwesomeIcon icon={faChevronLeft} className="!w-3 !h-3" />
              <FontAwesomeIcon icon={faChevronLeft} className="!w-3 !h-3 !-ml-1" />
            </button>
            
            {/* 이전 페이지 */}
            <button
              onClick={() => onPageChange(Math.max(0, franchisesPage.pageable.pageNumber - 1))}
              disabled={franchisesPage.pageable.pageNumber === 0}
              className="!px-2 !py-1.5 !text-sm !rounded !border !border-gray-300 hover:!bg-gray-50 disabled:!opacity-50 disabled:!cursor-not-allowed !transition-colors !duration-200"
            >
              <FontAwesomeIcon icon={faChevronLeft} className="!w-3 !h-3" />
            </button>
            
            {/* 페이지 번호들 */}
            <div className="!flex !space-x-0.5">
              {(() => {
                const currentPage = franchisesPage.pageable.pageNumber;
                const totalPages = franchisesPage.totalPages;
                let startPage = Math.max(0, currentPage - 4);
                let endPage = Math.min(totalPages - 1, startPage + 9);
                
                if (endPage - startPage < 9) {
                  startPage = Math.max(0, endPage - 9);
                }
                
                return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map(pageNum => (
                  <button
                    key={pageNum}
                    onClick={() => onPageChange(pageNum)}
                    className={`!px-2.5 !py-1 !text-sm !rounded !transition-colors !duration-200 ${
                      pageNum === currentPage
                        ? '!bg-blue-600 !text-white'
                        : 'hover:!bg-gray-100 !text-gray-700'
                    }`}
                  >
                    {pageNum + 1}
                  </button>
                ));
              })()}
            </div>
            
            {/* 다음 페이지 */}
            <button
              onClick={() => onPageChange(Math.min(franchisesPage.totalPages - 1, franchisesPage.pageable.pageNumber + 1))}
              disabled={franchisesPage.pageable.pageNumber === franchisesPage.totalPages - 1}
              className="!px-2 !py-1.5 !text-sm !rounded !border !border-gray-300 hover:!bg-gray-50 disabled:!opacity-50 disabled:!cursor-not-allowed !transition-colors !duration-200"
            >
              <FontAwesomeIcon icon={faChevronRight} className="!w-3 !h-3" />
            </button>
            
            {/* 맨 마지막으로 */}
            <button
              onClick={() => onPageChange(franchisesPage.totalPages - 1)}
              disabled={franchisesPage.pageable.pageNumber === franchisesPage.totalPages - 1}
              className="!px-2 !py-1.5 !text-sm !rounded !border !border-gray-300 hover:!bg-gray-50 disabled:!opacity-50 disabled:!cursor-not-allowed !transition-colors !duration-200"
            >
              <FontAwesomeIcon icon={faChevronRight} className="!w-3 !h-3" />
              <FontAwesomeIcon icon={faChevronRight} className="!w-3 !h-3 !-ml-1" />
            </button>
          </div>
        </div>
      )}

      <ChargeModal
        id={franchiseId}
        setId={setFranchiseId}
        companyName={companyName}
        setCompanyName={setCompanyName}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
      />
    </div>
  );
};

export default Franchise;
